/**
 * portfolio-policy.js — the SIZING axis for a portfolio.
 *
 * WHY THIS IS NOT `mandate`
 * `portfolio.mandate` (core/alpha-score.js) answers exactly one question: may the advisory ever
 * recommend an exit here. Two values, 'accumulate' and 'active', and both are load-bearing —
 * `classifyMandate` filters on them and ai-copilot stamps them into the prompt behind a hard rule.
 *
 * It cannot answer a second, orthogonal question: HOW is this sleeve's target arrived at.
 * Overloading `mandate` with a third value breaks two consumers. `classifyMandate` excludes
 * anything that is not exactly 'active', so a cyclical sleeve would silently drop out of alpha
 * scoring; and the copilot prompt would carry a token the hard rule does not describe. So the
 * sizing question gets its own field rather than a twelfth copy of an existing one.
 *
 *   weighted    rebalancing bands govern; drift proposes the buys.
 *   accumulate  a standing instruction the app does not argue with. Never proposed against,
 *               never trimmed by a plan. May carry a price TRIGGER, which only ever ALERTS.
 *   cyclical    size follows a declared cycle phase, never drawdown depth.
 *
 * ⚠ THERE IS NO DEFAULT. An undeclared sizing resolves to null and every caller must say so out
 * loud. A silent fallback to 'weighted' is precisely what let a drawdown-ranked crypto tranche
 * contradict an approved plan for three weeks without one warning on screen. (ABSENCE RULE)
 *
 * Pure: no imports, no state, no network. ctx is injected, exactly like core/alpha-score.js.
 */

export const SIZINGS = ['weighted', 'accumulate', 'cyclical'];

/** Declared sizing, or null. Null is a reportable state, never a reason to guess. */
export function sizingOfPortfolio(p) {
  if (!p || typeof p !== 'object') return null;
  return SIZINGS.includes(p.sizing) ? p.sizing : null;
}

/**
 * Map portfolioId -> sizing for the ACTIVE portfolios, plus the ids that failed to declare one.
 * Callers use `undeclared` to warn; they must not treat it as an empty list of problems.
 */
export function sizingIndex(portfolios) {
  const bySizing = Object.create(null);
  const undeclared = [];
  (portfolios || []).forEach(p => {
    if (!p || !p.id || p.status === 'closed') return;
    const s = sizingOfPortfolio(p);
    if (s) bySizing[p.id] = s;
    else undeclared.push({ id: p.id, name: p.name || p.id });
  });
  return { bySizing, undeclared };
}

/** Is this holding inside a sleeve a deployment plan may propose against? */
export function isDeployable(holding, index) {
  if (!holding) return false;
  const s = index && index.bySizing ? index.bySizing[holding.portfolioId] : null;
  // An accumulate sleeve is off the table. An undeclared one is NOT silently included:
  // the caller has already been handed `undeclared` and is required to warn about it.
  return s === 'weighted' || s === 'cyclical';
}

/**
 * A price trigger on an accumulate sleeve. It ALERTS; it never authorises a sell, because the
 * sleeve's whole purpose is that the owner does not argue with it month to month.
 *
 * trigger = { type:'price_level', ticker, level, currency, direction:'above'|'below', note }
 *
 * `price` must be quoted in the SAME currency as `trigger.currency`. A mismatch returns a
 * `currencyMismatch` result rather than comparing the numbers — SAP is quoted in EUR on XETRA
 * and in USD as an ADR, and a trigger that fires 16% early is worse than no trigger.
 */
export function evaluateTrigger(trigger, price, priceCurrency) {
  if (!trigger || trigger.type !== 'price_level') return null;
  const level = Number(trigger.level);
  const px = Number(price);
  if (!isFinite(level) || level <= 0) return { ok: false, why: 'no_level' };
  if (!isFinite(px) || px <= 0) return { ok: false, why: 'no_price' };
  if (trigger.currency) {
    // An unlabelled price is not a matching price. SAP quotes EUR on XETRA and USD as an ADR;
    // accepting an unlabelled 290 as EUR fires the alert on a number that is really ~EUR 250.
    if (!priceCurrency) return { ok: false, why: 'price_currency_unknown', want: trigger.currency };
    if (trigger.currency !== priceCurrency) {
      return { ok: false, why: 'currency_mismatch', want: trigger.currency, got: priceCurrency };
    }
  }
  const dir = trigger.direction === 'below' ? 'below' : 'above';
  const hit = dir === 'above' ? px >= level : px <= level;
  return {
    ok: true, hit, direction: dir, level, price: px,
    currency: trigger.currency || priceCurrency || null,
    distancePct: +(((px - level) / level) * 100).toFixed(2),
  };
}

/**
 * DOES THIS HOLDING'S PURCHASE MONEY PASS THROUGH THE TRACKED CASH LEDGER?
 *
 * `saveAddLot` debits `S.cash` for every investment lot and stamps the lot `funded: 'cash'`,
 * which is right for a brokered order paid from a balance the book knows about. It is wrong for
 * the SAP employee plan: that contribution is a payroll deduction that goes from the employer
 * straight to XETRA and never touches any account this book holds.
 *
 * MEASURED, 2026-09-10. The owner entered the July and August SAP lots and EUR cash went to
 * -1,973.74 on two `portfolio_buy` rows of 986.87 each. His eleven earlier SAP investment lots
 * carry no `funded` flag and moved no cash, so the same monthly fact was recorded two different
 * ways depending on which door it came through. Two consequences, both measured:
 *
 *   - Total Wealth understated by EUR 1,973.74. The 11.007 new shares are worth EUR 1,957.26 at
 *     177.82, so the reported change was -16.48 and the owner saw the number "not move at all".
 *   - `capital.js`'s plan_lots door counts an investment lot as EXTERNAL capital unless it is
 *     stamped `funded: 'cash'`. So eleven months of payroll contribution count as capital
 *     arriving and two months do not. `netExtCapital` understated by the same EUR 1,973.74.
 *
 * `twPnL = twUSD - netExtCapital` happened to survive because both sides fell by the same
 * amount. That is arithmetic luck, not a design.
 *
 * ROUTED ON THE BROKER, AND THAT IS AN INFERENCE STANDING IN FOR A FACT. The honest record
 * would be a declared field on the holding saying where its money comes from. `_IMPORT_KEYS` is
 * a DELETION list whose omissions have cost live data twice, so a new field is the highest-risk
 * line available tonight and the owner asked for the change that cannot do harm. XETRA is the
 * broker of exactly one holding in this book (SAP) and of no other, so the rule cannot misfire
 * on anything that exists. `feeModelFor` already sets this precedent for the `.L` suffix:
 * "A `venue` field would be the honest fact; the suffix stands in for it until there is one."
 *
 * A future payroll plan at a different broker would silently get the cash debit again. That is
 * why `cash.noNegative` grades the symptom rather than trusting this predicate to be complete.
 */
export function fundsFromCashLedger(holding) {
  const broker = String((holding && holding.broker) || '').toLowerCase();
  if (!broker) return true;                 // unknown broker: the ordinary case, debit as before
  return !broker.includes('xetra');
}
